/* eslint-disable jsx-a11y/media-has-caption */
import { useRef, useState, useEffect } from 'react';
import { useSetState, useVideo, useFullscreen } from 'react-use';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { isMobile, isIOS } from 'react-device-detect';
import cn from 'classnames';
// Helpers
import { computeDuration } from 'helpers/time';
// Ui
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import VideoSeek from 'ui/VideoPlayer/components/VideoSeek/VideoSeek';
import MicControl from 'ui/VideoPlayer/components/MicControl/MicControl';
import VolumeSeek from 'ui/VideoPlayer/components/VolumeSeek/VolumeSeek';
import CameraOption from 'ui/VideoPlayer/components/CameraOption/CameraOption';
import VideoSettings from 'ui/VideoPlayer/components/VideoSettings/VideoSettings';
import QualityOptions from 'ui/VideoPlayer/components/QualityOptions/QualityOptions';
import VideoControlButton from 'ui/VideoPlayer/components/VideoControlButton/VideoControlButton';
// Styles
import { useIdleHideControls } from './useIdleHideControls';
import styles from './VideoPlayer.module.scss';

type LocalAudio = {
  muted: boolean;
  volume: number;
};

type VideoAttributes = Omit<
  React.VideoHTMLAttributes<any>,
  'controls' | 'placeholder'
>;

type QualityProps =
  | {
      qualityOptions: { label: string; value: number }[];
      onQualityChange: (quality: number) => void;
    }
  | {
      qualityOptions?: never;
      onQualityChange?: never;
    };

type VolumeProps =
  | {
      onVolume?: (volume: number) => void;
      onMute: () => void;
      onUnMute: () => void;
    }
  | {
      onVolume?: never;
      onMute?: never;
      onUnMute?: never;
    };

export type VideoPlayerProps = {
  onPlay?: () => void;
  onPause?: () => void;
  onCameraToggle?: () => void;
  onPlayNext?: () => void;
  withoutAspectRatio?: boolean;
  debug?: boolean;
  pausePoster?: string;
  wrapperClassName?: string;
  volumeControl: 'mic' | 'speaker';
  labels?: JSX.Element | null;
  settings?: JSX.Element;
  placeholder?: JSX.Element;
  videoClassName?: string;
  videoPoster?: string;
  initialDurationSec?: number;
  initialTimeSec?: number;
} & QualityProps &
  VolumeProps &
  VideoAttributes;

const VideoPlayer = ({
  onPlay,
  onPause,
  onVolume,
  onMute,
  onUnMute,
  onPlayNext,
  qualityOptions,
  onQualityChange,
  onCameraToggle,
  src,
  withoutAspectRatio,
  debug,
  pausePoster,
  wrapperClassName,
  volumeControl = 'speaker',
  labels,
  settings,
  placeholder,
  videoClassName,
  videoPoster,
  initialDurationSec,
  initialTimeSec,
  ...rest
}: VideoPlayerProps) => {
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [localAudio, setLocalAudio] = useSetState<LocalAudio>({
    muted: false,
    volume: 1,
  });
  const [playToggleClicked, setPlayToggleClicked] = useState<boolean>(false);
  const previousPlayingRef = useRef<boolean>(false);
  const playButtonInitiatedRef = useRef<boolean>(false);
  const didSeekInitialTimeRef = useRef(false);

  const [video, state, controls, videoRef] = useVideo(
    <video
      className={videoClassName ? videoClassName : styles.video}
      src={src || ''}
      poster={rest.poster || ''}
      disablePictureInPicture
      playsInline
      {...rest}
      controls={false}
      data-testid="video-player"
      onEnded={onPlayNext}
    />
  );

  const rootRef = useRef<HTMLDivElement | null>(null);
  const isMobileIOS = isMobile && isIOS;

  // show fullscreen option for all cases but not for mobile IOS and allow a fullscreen
  // fallback for mobile IOS only if it's a stream (assuming that there is no 'src' value)
  const isFullscreenOptionAvailable = !isMobileIOS || (isMobileIOS && !src);
  const isUseFullscreenEnable = fullscreen && !isMobileIOS;
  const isFullscreenFallbackEnable = fullscreen && isMobileIOS;

  useFullscreen(rootRef, isUseFullscreenEnable, {
    onClose: () => setFullscreen(false),
  });

  const {
    duration: stateDuration,
    muted,
    time,
    volume,
    paused,
    playing,
    buffered,
  } = state;

  const { controlsHidden, idleHandlers } = useIdleHideControls(
    !playing || paused
  );

  const elDuration = videoRef?.current?.duration ?? NaN;
  const duration: number =
    stateDuration > 0 && Number.isFinite(stateDuration)
      ? stateDuration
      : Number.isFinite(elDuration) && elDuration > 0
      ? elDuration
      : typeof initialDurationSec === 'number' &&
        Number.isFinite(initialDurationSec) &&
        initialDurationSec > 0
      ? initialDurationSec
      : 0;

  // Reset tracking when video source changes
  useEffect(() => {
    previousPlayingRef.current = false;
    playButtonInitiatedRef.current = false;
    didSeekInitialTimeRef.current = false;
  }, [src]);

  useEffect(() => {
    if (didSeekInitialTimeRef.current) return;
    if (
      typeof initialTimeSec !== 'number' ||
      !Number.isFinite(initialTimeSec) ||
      initialTimeSec < 0 ||
      !(duration > 0)
    ) {
      return;
    }

    const time = Math.min(initialTimeSec, duration);
    const el = videoRef?.current;
    if (el) {
      el.currentTime = time;
    }
    controls.seek(time);
    didSeekInitialTimeRef.current = true;
  }, [initialTimeSec, duration, src, controls, videoRef]);

  // Track play events including autoplay
  // This ensures onPlay is called when video starts playing, whether via button click or autoplay
  useEffect(() => {
    // Trigger onPlay when video transitions from paused to playing
    // This covers both manual play button clicks and autoplay
    const justStartedPlaying = playing && !previousPlayingRef.current;

    if (justStartedPlaying && onPlay) {
      // If play button was clicked, onPlay was already called in handlePlayToggle
      // Only call onPlay here if it wasn't initiated by button click (i.e., autoplay)
      if (!playButtonInitiatedRef.current) {
        onPlay();
      }
      // Reset the flag after handling the transition
      playButtonInitiatedRef.current = false;
    }

    // Update previous state for next comparison
    previousPlayingRef.current = playing;
  }, [playing, onPlay]);

  const isBuffering = Boolean(!paused && !playing && src);
  const isQualityOptionsVisible = Boolean(qualityOptions && onQualityChange);
  // use local value if 'onVolume' and related props exists.
  // necessary to prevent any video volume change. use case: changing microphone volume
  const volumeValue = onVolume ? localAudio.volume : volume;
  const mutedValue = onVolume ? localAudio.muted : muted;
  const isMicVolumeControl = volumeControl === 'mic';
  // because mic is only for streamer view we can use this

  const handlePlayToggle = () => {
    setPlayToggleClicked(true);
    if (playing) {
      controls.pause();

      if (onPause) {
        onPause();
      }
    } else {
      // Mark that play was initiated by button click
      playButtonInitiatedRef.current = true;
      controls.play();

      // Call onPlay immediately for button clicks
      if (onPlay) {
        onPlay();
      }
    }
  };

  const handleVideoSeekChange = (time: number) => {
    controls.seek(time);
  };

  const handleVolumeSeekChange = (volume: number) => {
    if (onVolume) {
      onVolume(volume);
      setLocalAudio({ volume });
    } else {
      controls.volume(volume);
    }
  };

  const handleVolumeSeekMute = () => {
    if (onMute) {
      onMute();
      setLocalAudio({ muted: true });
    } else {
      controls.mute();
    }
  };

  const handleVolumeSeekUnmute = () => {
    if (onUnMute) {
      onUnMute();
      setLocalAudio({ muted: false });
    } else {
      controls.unmute();
    }
  };

  const handleQualityChange = (quality: number) => {
    if (onQualityChange) {
      onQualityChange(quality);
    }
  };

  const handleFullscreenToggle = () => {
    // Safari and Chrome on mobile iOS does not allow web pages been full screen
    if (isMobileIOS) {
      const body = document.body;
      // adding a z-index to the 'main' tag is necessary to override the z-index of the fixed header. without this, the fullscreen will not be complete and will be overlapped by the header
      const mainTagStyle = document.querySelector('main')?.style;
      // adding a z-index to the 'hubspot' widget is necessary to override the z-index of the 'hubspot'. without this, the widget will overlapped the fullscreen mode
      const hubspotWidgetStyle = document.getElementById(
        'hubspot-messages-iframe-container'
      )?.style;

      if (fullscreen) {
        setFullscreen(false);
        enableBodyScroll(body);

        if (mainTagStyle) {
          mainTagStyle.zIndex = '';
        }

        if (hubspotWidgetStyle) {
          hubspotWidgetStyle.zIndex = '';
        }
      } else {
        setFullscreen(true);
        disableBodyScroll(body);

        if (mainTagStyle) {
          mainTagStyle.zIndex = '999';
        }

        if (hubspotWidgetStyle) {
          hubspotWidgetStyle.zIndex = '-999';
        }
      }
    } else {
      setFullscreen((prev) => !prev);
    }
  };

  return (
    <div
      className={cn(
        styles.root,
        {
          [styles.withoutAspectRatio]: withoutAspectRatio,
          [styles.fullscreen]: isFullscreenFallbackEnable,
        },
        wrapperClassName
      )}
      ref={rootRef}
      {...idleHandlers}
    >
      {labels}

      {placeholder && (
        <div className={styles.placeholderWrapper}>{placeholder}</div>
      )}

      {video}

      {!!videoPoster && !playToggleClicked && !playing && (
        <img
          src={videoPoster}
          alt="Video poster"
          className={styles.videoPoster}
        />
      )}

      {(!!pausePoster || Boolean(paused && pausePoster)) && (
        <img
          src={pausePoster}
          alt="Pause poster"
          className={styles.coverImage}
        />
      )}

      {debug && (
        <pre className={styles.debug}>{JSON.stringify(state, null, 2)}</pre>
      )}

      {isBuffering && (
        <FourDotsLoader
          wrapperClassName={styles.loader}
          overlayBackgroundColor="transparent"
          loaderWrapperBackgroundColor="transparent"
        />
      )}

      <div
        className={cn(styles.controls, {
          [styles.controlsIdleHidden]: controlsHidden,
        })}
      >
        {!!src && (
          <VideoSeek
            buffered={buffered}
            time={time}
            duration={duration}
            onSeek={handleVideoSeekChange}
          />
        )}

        <div className={styles.controlsBottom}>
          <div className={styles.left}>
            <VideoControlButton
              icon={playing ? 'pause' : 'play'}
              title={playing ? 'Pause' : 'Play'}
              onClick={handlePlayToggle}
              className={styles.controlsButton}
            />

            {onPlayNext && (
              <VideoControlButton
                icon="play-next"
                title="Play next"
                onClick={onPlayNext}
                className={styles.controlsButton}
              />
            )}

            {isMicVolumeControl ? (
              <MicControl
                muted={mutedValue}
                onMute={handleVolumeSeekMute}
                onUnMute={handleVolumeSeekUnmute}
              />
            ) : (
              <VolumeSeek
                volume={volumeValue}
                muted={mutedValue}
                onSeek={handleVolumeSeekChange}
                onMute={handleVolumeSeekMute}
                onUnMute={handleVolumeSeekUnmute}
              />
            )}

            <time className={styles.time}>
              {computeDuration(time)} / {computeDuration(duration)}
            </time>
          </div>

          <div className={styles.right}>
            {settings && <VideoSettings settings={settings} />}

            {isQualityOptionsVisible && qualityOptions?.length && (
              <QualityOptions
                options={qualityOptions}
                onChange={handleQualityChange}
              />
            )}

            {onCameraToggle && <CameraOption onToggle={onCameraToggle} />}
            {isFullscreenOptionAvailable && (
              <VideoControlButton
                icon="fullscreen"
                title={fullscreen ? 'Exit full screen' : 'Full screen'}
                onClick={handleFullscreenToggle}
                className={styles.controlsButton}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
